import { useI18n } from '../../i18n';
// ============================================
// LoyaltyPage Component
// Loyalty program and member management
// ============================================

import { useState, useMemo, useEffect, useRef } from 'react';
import { useLockBodyScroll } from '../../hooks/useLockBodyScroll';
import { useSearchParams } from 'react-router-dom';
import { PageHeader, PageContainer, Pagination } from '../../components/shared';
import {
  useLoyaltyAccounts,
  useLoyaltyStats,
  getTierInfo,
  usePointsHistory,
  useAdjustAccountPoints,
  useGrantBonusPoints,
} from '../../hooks';
import type { LoyaltyAccount, LoyaltyTier } from '@conai/shared-types';
import { TierConfigSection } from './components/TierConfigSection';
import { formatMoney } from '../../utils/currency';

// ============================================
// Types
// ============================================

/**
 * `tier_info` của một dòng trong `GET /loyalty/accounts/` (BE 23/09/2026).
 * Chỉ khai những field màn này dùng.
 */
interface MemberTierInfo {
  next_tier_display?: string | null;
  /** Chuỗi decimal — quy ước tiền tệ chung của API. `null` khi đã ở hạng cao nhất. */
  spend_to_next_tier?: string | number | null;
  progress_percent?: number | null;
  /**
   * Đồng tiền ĐANG ĐO thang hạng. `null` khi khách chưa mua gì (ngưỡng rơi về
   * mặc định). Hiện đơn vị theo field này, ĐỪNG đoán theo cửa hàng đang chọn:
   * danh sách có thể gồm khách của nhiều thị trường.
   */
  currency?: string | null;
}

interface MemberItem {
  id: string;
  customerId: string;
  customerName: string;
  tier: LoyaltyTier;
  points: number;
  lifetimePoints: number;
  /**
   * TIỀN đã tiêu tích luỹ — thứ QUYẾT ĐỊNH hạng (điểm thì không).
   *
   * Chuỗi decimal (`"1200000.00"`), giữ nguyên dạng chuỗi để không mất xu; parse
   * trước khi so sánh hay sắp xếp. Trước 23/09/2026 màn này không hiện nó, nên
   * admin nhìn bảng thấy "1.000 lifetime points, hạng Bronze" mà không có cách
   * nào biết vì sao — con số quyết định không có mặt ở đâu cả.
   */
  lifetimeSpend: string | number | null;
  tierInfo: MemberTierInfo | null;
  joinedAt: string;
}

interface LoyaltyDetailsModalProps {
  member: MemberItem;
  onClose: () => void;
}

function LoyaltyDetailsModal({ member, onClose }: LoyaltyDetailsModalProps) {
  const { t } = useI18n();
  useLockBodyScroll(true);
  const { data: historyData, isLoading } = usePointsHistory(member.id, { page_size: 20 });

  const history = historyData?.results ?? [];

  // ---- Chỉnh điểm thủ công (sheet 2 mục #44) ----
  // `member` là snapshot từ danh sách nên số dư trên modal chỉ cập nhật sau
  // khi list refetch (mutation đã invalidate) — history bên dưới thì tươi ngay.
  const { mutate: adjustPoints, isPending: isAdjusting } = useAdjustAccountPoints();
  const { mutate: grantBonus, isPending: isGrantingBonus } = useGrantBonusPoints();
  /**
   * Ba chế độ đi qua HAI endpoint khác nhau:
   *   add / deduct -> POST /loyalty/adjust/  (entry ADJUST, points ±)
   *   bonus        -> POST /loyalty/bonus/   (entry BONUS, points luôn dương)
   *
   * Gộp vào một hàng nút vì ba thao tác cùng cần đúng hai ô: số điểm và lý do.
   * Nhưng KHÔNG gộp ở tầng gọi API: `/loyalty/adjust/` không nhận `entry_type`
   * nên không có cách nào sinh entry BONUS từ đó, và hai loại entry này phải
   * tách được trong báo cáo — ADJUST là sửa sai, BONUS là chi phí khuyến mãi.
   */
  const [adjustMode, setAdjustMode] = useState<'add' | 'deduct' | 'bonus'>('add');
  const [adjustAmount, setAdjustAmount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustError, setAdjustError] = useState<string | null>(null);
  const isSubmittingPoints = isAdjusting || isGrantingBonus;

  const handleAdjustSubmit = () => {
    setAdjustError(null);
    const amount = Number.parseInt(adjustAmount, 10);
    if (!Number.isFinite(amount) || amount <= 0) {
      setAdjustError(t('adjustPointsErrorPoints'));
      return;
    }
    if (!adjustReason.trim()) {
      setAdjustError(t('adjustPointsErrorReason'));
      return;
    }

    const handlers = {
      onSuccess: () => {
        setAdjustAmount('');
        setAdjustReason('');
      },
      onError: (err: unknown) => {
        setAdjustError(err instanceof Error ? err.message : t('adjustPointsErrorGeneric'));
      },
    };

    if (adjustMode === 'bonus') {
      grantBonus(
        {
          customerId: member.customerId,
          accountId: member.id,
          points: amount,
          reason: adjustReason.trim(),
        },
        handlers
      );
      return;
    }

    adjustPoints(
      {
        customerId: member.customerId,
        accountId: member.id,
        points: adjustMode === 'add' ? amount : -amount,
        reason: adjustReason.trim(),
      },
      handlers
    );
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 backdrop-blur-sm" style={{ backgroundColor: 'rgba(15,23,42,0.45)' }} onClick={onClose}>
      <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl flex flex-col max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-100">
          <div>
            <h3 className="text-lg font-bold text-slate-900">{t('loyaltyProgram')} - {t('memberDetail')}</h3>
            <p className="text-sm text-slate-500">{member.customerName}</p>
          </div>
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-full text-slate-400 hover:bg-slate-100 transition-colors">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
          </button>
        </div>

        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          {/* Member Stats — mobile: Tier full hàng, 2 ô điểm chung hàng dưới */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
            <div className="col-span-2 min-w-0 rounded-xl bg-slate-50 p-4 sm:col-span-1">
              <span className="text-xs font-semibold text-slate-500 uppercase">{t('tier')}</span>
              <div className="mt-2"><TierBadge tier={member.tier} /></div>
            </div>
            <div className="min-w-0 rounded-xl bg-slate-50 p-4">
              <span className="text-xs font-semibold text-slate-500 uppercase">{t('currentPoints')}</span>
              <div className="mt-1 break-words text-lg font-bold text-slate-900 sm:text-xl">{member.points.toLocaleString()}</div>
            </div>
            <div className="min-w-0 rounded-xl bg-slate-50 p-4">
              <span className="text-xs font-semibold text-slate-500 uppercase">{t('lifetimePoints')}</span>
              <div className="mt-1 break-words text-lg font-bold text-slate-900 sm:text-xl">{member.lifetimePoints.toLocaleString()}</div>
            </div>
            {/* Căn cứ xếp hạng. Hai ô điểm ở trên KHÔNG quyết định hạng — để
                chúng đứng một mình là mời người đọc suy ra quan hệ không có. */}
            <div className="min-w-0 rounded-xl bg-slate-50 p-4">
              <span className="text-xs font-semibold text-slate-500 uppercase">{t('lifetimeSpend')}</span>
              <div className="mt-1 break-words text-lg font-bold text-slate-900 sm:text-xl">
                {formatSpend(member.lifetimeSpend, member.tierInfo?.currency) ?? '—'}
              </div>
            </div>
            <div className="min-w-0 rounded-xl bg-slate-50 p-4">
              <span className="text-xs font-semibold text-slate-500 uppercase">{t('toNextTier')}</span>
              <div className="mt-2"><ToNextTierCell info={member.tierInfo} /></div>
            </div>
          </div>

          {/* Chỉnh điểm thủ công — POST /loyalty/adjust/ (points ±, reason
              bắt buộc vào audit log; BE 03/09) (mục #44) */}
          <div className="rounded-xl border border-slate-200 p-4">
            <h4 className="font-bold text-slate-800 text-sm mb-3">{t('adjustPointsTitle')}</h4>
            <div className="flex flex-wrap items-start gap-2">
              <div className="flex rounded-lg border border-slate-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setAdjustMode('add')}
                  className={`px-3 py-2 text-sm font-semibold transition-colors ${
                    adjustMode === 'add' ? 'bg-emerald-50 text-emerald-700' : 'text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  {t('adjustPointsAdd')}
                </button>
                <button
                  type="button"
                  onClick={() => setAdjustMode('deduct')}
                  className={`px-3 py-2 text-sm font-semibold transition-colors ${
                    adjustMode === 'deduct' ? 'bg-red-50 text-red-700' : 'text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  {t('adjustPointsDeduct')}
                </button>
                <button
                  type="button"
                  onClick={() => setAdjustMode('bonus')}
                  className={`border-l border-slate-200 px-3 py-2 text-sm font-semibold transition-colors ${
                    adjustMode === 'bonus' ? 'bg-amber-50 text-amber-700' : 'text-slate-500 hover:bg-slate-50'
                  }`}
                >
                  {t('adjustPointsBonus')}
                </button>
              </div>
              <input
                type="number"
                min={1}
                value={adjustAmount}
                onChange={(e) => setAdjustAmount(e.target.value)}
                placeholder={t('points') || 'Points'}
                className="w-28 rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary-400"
              />
              <input
                type="text"
                value={adjustReason}
                onChange={(e) => setAdjustReason(e.target.value)}
                placeholder={t('adjustPointsReasonPlaceholder')}
                className="min-w-[180px] flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary-400"
              />
              <button
                type="button"
                onClick={handleAdjustSubmit}
                disabled={isSubmittingPoints}
                className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white hover:bg-primary-700 transition-colors disabled:opacity-60"
              >
                {isSubmittingPoints ? t('saving') : t('adjustPointsSubmit')}
              </button>
            </div>
            {/* Nói rõ chế độ "Tặng" ghi ra loại entry khác — nhìn ba cái nút
                giống nhau thì không ai đoán được là hai bút toán khác nghĩa. */}
            {adjustMode === 'bonus' ? (
              <p className="mt-2 text-xs text-amber-700">{t('adjustPointsBonusHint')}</p>
            ) : null}
            {adjustError ? <p className="mt-2 text-xs text-red-600">{adjustError}</p> : null}
          </div>

          {/* Points History */}
          <div>
            <h4 className="font-bold text-slate-800 text-sm mb-3">{t('pointsTransactionHistory')}</h4>
            {isLoading ? (
              <div className="flex items-center justify-center py-10">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : history.length === 0 ? (
              <div className="text-center text-sm text-slate-400 py-10 border border-dashed border-slate-200 rounded-xl bg-slate-50/50">
                {t('noPointTransactionsFound')}
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-slate-100 bg-white">
                <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-slate-150 text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-2.5 text-left font-semibold text-slate-600">{t('date') || 'Date'}</th>
                      <th className="px-4 py-2.5 text-left font-semibold text-slate-600">{t('type') || 'Type'}</th>
                      <th className="px-4 py-2.5 text-right font-semibold text-slate-600">{t('points') || 'Points'}</th>
                      <th className="px-4 py-2.5 text-left font-semibold text-slate-600">{t('description')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {history.map((entry: any) => {
                      const isPositive = entry.points > 0;
                      return (
                        <tr key={entry.id} className="hover:bg-slate-50 transition-colors">
                          <td className="px-4 py-3 text-slate-500">
                            {new Date(entry.created_at || entry.timestamp).toLocaleString()}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                              entry.entry_type === 'EARN' ? 'bg-green-50 text-green-700' :
                              entry.entry_type === 'REDEEM' ? 'bg-red-50 text-red-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {entry.entry_type}
                            </span>
                          </td>
                          <td className={`px-4 py-3 text-right font-semibold ${isPositive ? 'text-green-600' : 'text-red-500'}`}>
                            {isPositive ? `+${entry.points}` : entry.points}
                          </td>
                          <td className="px-4 py-3 text-slate-600 truncate max-w-[200px]" title={entry.description}>
                            {entry.description}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-slate-100 px-6 py-4 flex justify-end bg-slate-50/50">
          <button onClick={onClose} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-colors">
            {t('close')}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================
// Stats Component
// ============================================

function StatsCard({ title, value, color }: { title: string; value: string | number; color: string }) {
  const { t } = useI18n();
  return (
    <div className="rounded-xl bg-white p-5 shadow-sm border border-slate-100">
      <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{title}</p>
      <p className={`mt-2 text-2xl font-bold ${color}`}>{value}</p>
    </div>
  );
}

// ============================================
// Tier Badge
// ============================================

/**
 * Tiền từ BE là CHUỖI decimal (`"1200000.00"`) — quy ước chung của API, giữ
 * chuỗi để không mất xu khi truyền. Parse ở đúng chỗ hiển thị.
 *
 * `null` khi không có số để hiện; caller vẽ dấu gạch chứ không vẽ "0", vì 0 và
 * "chưa biết" là hai chuyện khác nhau.
 */
function formatSpend(value: string | number | null | undefined, currency?: string | null): string | null {
  if (value == null) return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  return formatMoney(parsed, { currency: currency ?? undefined });
}

/**
 * "Còn bao nhiêu nữa lên hạng" + thanh tiến độ, cho MỘT dòng.
 *
 * Ba trạng thái, đừng gộp:
 *   - không có `tier_info`            -> chưa biết: gạch ngang, không thanh.
 *   - `spend_to_next_tier` null/<= 0  -> đã ở hạng cao nhất.
 *   - có số                           -> số tiền còn thiếu + tiến độ.
 *
 * Tiến độ lấy `progress_percent` của BE, không tự tính: ngưỡng cấu hình riêng
 * theo từng tiền tệ nên mọi phép tính ở client đều có đường lệch.
 */
function ToNextTierCell({ info }: { info: MemberTierInfo | null }) {
  const { t } = useI18n();
  if (!info) return <span className="text-slate-400">—</span>;

  const remaining = formatSpend(info.spend_to_next_tier, info.currency);
  const isMaxTier = info.spend_to_next_tier == null || Number(info.spend_to_next_tier) <= 0;

  if (isMaxTier) {
    return <span className="text-xs font-medium text-emerald-600">{t('maxTierReached')}</span>;
  }
  if (!remaining) return <span className="text-slate-400">—</span>;

  const percent = Math.min(100, Math.max(0, Number(info.progress_percent ?? 0)));

  return (
    <div className="flex flex-col items-end gap-1">
      <span className="font-medium text-slate-900">{remaining}</span>
      {info.next_tier_display ? (
        <span className="text-xs text-slate-500">→ {info.next_tier_display}</span>
      ) : null}
      <div className="h-1.5 w-24 overflow-hidden rounded-full bg-slate-200">
        <div className="h-full rounded-full bg-primary-600" style={{ width: `${percent}%` }} />
      </div>
    </div>
  );
}

function TierBadge({ tier }: { tier: LoyaltyTier }) {
  const { t } = useI18n();
  const info = getTierInfo(tier);
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${info.bgColor} ${info.color}`}>
      <span>{info.icon}</span>
      {info.label}
    </span>
  );
}

// ============================================
// Table Component
// ============================================

function MemberTable({
  data, isLoading, onView,
  currentPage, totalPages, totalItems, pageSize, onPageChange,
}: {
  data: MemberItem[];
  isLoading: boolean;
  onView: (item: MemberItem) => void;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
}) {
  const { t } = useI18n();
  if (isLoading) {
    return (
      <div className="rounded-xl bg-white shadow-sm border border-slate-100 p-8">
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl bg-white shadow-sm border border-slate-100 overflow-hidden">
      <div className="overflow-x-auto">
      <table className="min-w-full divide-y divide-slate-200">
        <thead className="bg-slate-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-600">{t('member')}</th>
            <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-600">{t('tier')}</th>
            <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">{t('currentPoints')}</th>
            <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">{t('lifetimePoints')}</th>
            {/* Hai cột dưới mới là căn cứ xếp hạng. Hạng tính trên TIỀN đã tiêu,
                không phải điểm — thiếu chúng thì cột Tier là một đáp án không
                có lời giải. */}
            <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">{t('lifetimeSpend')}</th>
            <th className="px-6 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-600">{t('toNextTier')}</th>
            <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-600">{t('memberSince')}</th>
            <th className="px-6 py-3 text-center text-xs font-semibold uppercase tracking-wider text-slate-600">{t('loyaltyActions')}</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-200">
          {data.map((item) => (
            <tr key={item.id} className="hover:bg-slate-50 transition-colors">
              <td className="px-6 py-4">
                <span className="font-medium text-slate-900">{item.customerName}</span>
              </td>
              <td className="px-6 py-4 text-center">
                <TierBadge tier={item.tier} />
              </td>
              <td className="px-6 py-4 text-right font-medium text-slate-900">{item.points.toLocaleString()}</td>
              <td className="px-6 py-4 text-right text-slate-600">{item.lifetimePoints.toLocaleString()}</td>
              <td className="px-6 py-4 text-right font-medium text-slate-900">
                {formatSpend(item.lifetimeSpend, item.tierInfo?.currency) ?? '—'}
              </td>
              <td className="px-6 py-4 text-right text-slate-600">
                <ToNextTierCell info={item.tierInfo} />
              </td>
              <td className="px-6 py-4 text-center text-sm text-slate-500">
                {new Date(item.joinedAt).toLocaleDateString()}
              </td>
              <td className="px-6 py-4 text-center">
                <button onClick={() => onView(item)} className="text-primary-600 hover:text-primary-700 text-sm font-medium">
                  {t('details')}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      {totalItems > 0 && (
        <div className="border-t border-slate-100">
          <Pagination
            variant="numbered"
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={onPageChange}
            itemLabel="members"
          />
        </div>
      )}
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export function LoyaltyPage() {
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    setCurrentPage(Number(searchParams.get('page') || '1'));
  }, [searchParams]);

  // Selected Member for Modal
  const [selectedMember, setSelectedMember] = useState<MemberItem | null>(null);

  // Hai việc khác hẳn nhau trên cùng một trang: xem/chỉnh từng thành viên, và
  // cấu hình ngưỡng hạng cho cả chuỗi. Tách tab để khối cấu hình không đẩy bảng
  // thành viên xuống dưới màn hình.
  const [activeTab, setActiveTab] = useState<'members' | 'tierConfig'>('members');

  // API Queries
  const { data: accountsData, isLoading, error } = useLoyaltyAccounts({
    page: currentPage,
    limit: pageSize,
  } as any);
  const { stats, isLoading: statsLoading } = useLoyaltyStats();

  // Map accounts
  const members: MemberItem[] = useMemo(() => {
    if (!accountsData?.results) {
      return [];
    }
    return accountsData.results.map((acc: any) => ({
      id: acc.id,
      customerId: acc.customer_id,
      customerName: acc.customer_name ?? 'Unknown',
      tier: acc.tier,
      points: acc.points_balance,
      lifetimePoints: acc.lifetime_points,
      lifetimeSpend: acc.lifetime_spend ?? null,
      tierInfo: acc.tier_info ?? null,
      joinedAt: acc.created_at,
    }));
  }, [accountsData]);

  // Pagination
  const totalItems = accountsData?.count ?? 0;
  const totalPages = Math.ceil(totalItems / pageSize);

  // Handlers
  const handleView = (item: MemberItem) => setSelectedMember(item);

  return (
    <PageContainer>
      <PageHeader
        title={t('loyaltyProgram')}
        subtitle={t('manageLoyaltyMembersTiersAndPointRe')}
      />

      {error && (
        <div className="mt-4 rounded-lg bg-red-50 p-4 text-sm text-red-700">
          <p className="font-medium">{t('failedToLoadLoyaltyData')}</p>
        </div>
      )}

      <div className="mt-6 grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatsCard title={t('totalMembers')} value={statsLoading ? '...' : stats.totalMembers} color="text-purple-700" />
        <StatsCard title={t('platinum')} value={statsLoading ? '...' : stats.platinum} color="text-slate-700" />
        <StatsCard title={t('gold')} value={statsLoading ? '...' : stats.gold} color="text-amber-600" />
        <StatsCard title={t('silver')} value={statsLoading ? '...' : stats.silver} color="text-gray-500" />
        <StatsCard title={t('bronze')} value={statsLoading ? '...' : stats.bronze} color="text-orange-600" />
      </div>

      <div className="mt-6 flex gap-1 border-b border-slate-200">
        {(['members', 'tierConfig'] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveTab(tab)}
            className={`-mb-px border-b-2 px-4 py-2.5 text-sm font-semibold transition-colors ${
              activeTab === tab
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab === 'members' ? t('loyaltyTabMembers') : t('loyaltyTabTierConfig')}
          </button>
        ))}
      </div>

      {activeTab === 'members' ? (
        <div className="mt-6">
          <MemberTable
            data={members}
            isLoading={isLoading}
            onView={handleView}
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={(page) => {
              setCurrentPage(page);
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                next.set('page', String(page));
                return next;
              });
            }}
          />
        </div>
      ) : (
        <div className="mt-6">
          <TierConfigSection />
        </div>
      )}

      {selectedMember && (
        <LoyaltyDetailsModal member={selectedMember} onClose={() => setSelectedMember(null)} />
      )}
    </PageContainer>
  );
}
